import { toJalaali, toGregorian, jalaaliMonthLength, IJalaaliDate, IGregorianDate } from './jalaali'
import { toHijri, fromHijri, hijriMonthLength, IHijriDate } from './hijri'
import { J_MONTHS, G_MONTHS, H_MONTHS } from '../configs/names'
import { toFaDigits } from '../helpers/digits'

// Calendar data shaping, kept clear of the DOM: which calendars exist, how long
// a month is in each, and how one date reads in all three. The converter page
// and the shared date picker both read from here.

export type System = 'jalaali' | 'gregorian' | 'hijri'

export interface IDateParts {
    year: number
    month: number
    day: number
}

export interface IConverted {
    j: IJalaaliDate
    g: IGregorianDate
    h: IHijriDate
    wd: number // Persian weekday index, 0 = Saturday, to index J_WEEKDAYS
}

export const SYSTEMS: System[] = ['jalaali', 'gregorian', 'hijri']

export const SYSTEM_NAMES: Record<System, string> = {
    jalaali: 'شمسی',
    gregorian: 'میلادی',
    hijri: 'قمری',
}

export const MONTHS: Record<System, string[]> = {
    jalaali: J_MONTHS,
    gregorian: G_MONTHS,
    hijri: H_MONTHS,
}

// Bounds for the year field. The low end keeps every conversion inside the
// range JS Date treats literally — a Gregorian year under 100 is remapped into
// the 1900s — and hijri/jalaali year 1 already lands in 622 CE, so only the
// Gregorian field needs a floor above 1.
export const YEAR_RANGE: Record<System, { min: number; max: number }> = {
    jalaali: { min: 1, max: 1600 },
    gregorian: { min: 622, max: 2200 },
    hijri: { min: 1, max: 1700 },
}

const isLeapGregorian = (gy: number) => gy % 4 === 0 && !(gy % 100 === 0 && gy % 400 !== 0)
const G_LENGTHS = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31]

// Days in the given month. The hijri answer is the adjusted length, so a
// moon-sighting correction changes the days the picker offers — a 29-day
// رمضان stops listing a 30th.
export function monthLength(system: System, year: number, month: number) {
    if (system === 'jalaali') return jalaaliMonthLength(year, month)
    if (system === 'hijri') return hijriMonthLength(year, month)
    return month === 2 && isLeapGregorian(year) ? 29 : G_LENGTHS[month - 1]
}

// One date in all three calendars. Everything routes through the Gregorian
// date, and both hijri directions go through services/hijri, so whatever
// adjustments are active apply here exactly as they do in the month grid.
export function convert(system: System, { year, month, day }: IDateParts): IConverted {
    const g = system === 'jalaali' ? toGregorian(year, month, day)
        : system === 'hijri' ? fromHijri(year, month, day)
            : { gy: year, gm: month, gd: day }
    const date = new Date(g.gy, g.gm - 1, g.gd)
    return {
        j: toJalaali(g.gy, g.gm, g.gd),
        g,
        h: toHijri(g.gy, g.gm, g.gd),
        // getDay() is Sunday-first; the Persian week starts on Saturday.
        wd: (date.getDay() + 1) % 7,
    }
}

// The same date read in one chosen calendar.
export function parts(system: System, c: IConverted): IDateParts {
    if (system === 'jalaali') return { year: c.j.jy, month: c.j.jm, day: c.j.jd }
    if (system === 'hijri') return { year: c.h.hy, month: c.h.hm, day: c.h.hd }
    return { year: c.g.gy, month: c.g.gm, day: c.g.gd }
}

export function format(system: System, c: IConverted) {
    const { year, month, day } = parts(system, c)
    return `${toFaDigits(day)} ${MONTHS[system][month - 1]} ${toFaDigits(year)}`
}
